/* eslint-disable max-lines */
import { Dispatch as ReactDispatch, ReducerAction } from 'react';
import { ReferralUploadFile } from '../steps/ReferralStep/ReferralStep';
import { PaymentError } from '../util/usePostBookingCommand';
import {
  FormState,
  Practice,
  FormMeta,
  ApptDoctor,
  Appointment,
  User,
  Patient,
  PaymentMethod,
  ConfirmedBooking,
  AppointmentTypeState,
  VoucherAdditionalQuestion,
} from './FormState';

export type Dispatch = ReactDispatch<ReducerAction<typeof formStateReducer>>;

export interface FormStateReducerProps {
  state: FormState;
  dispatch: Dispatch;
}

export type SimpleAction = 'back' | 'back-to-start' | 'assert-vaccine-eligibility';

export type Action =
  | { type: 'overwrite-state'; state: FormState }
  | { type: SimpleAction }
  | { type: 'continue'; fromStepIndex: number }
  | { type: 'update-practice'; partial: Partial<Practice> }
  | { type: 'update-meta'; partial: Partial<FormMeta> }
  | { type: 'update-practitioner'; apptDoctor?: ApptDoctor }
  | { type: 'update-appointment'; partial: Partial<Appointment> }
  | { type: 'update-user'; partial: Partial<User> }
  | { type: 'update-patient'; partial: Partial<Patient> }
  | { type: 'update-payment-method'; partial: Partial<PaymentMethod> }
  | { type: 'validation-error'; errors: object }
  | { type: 'goto-step'; stepIndex: number }
  | {
      type: 'booking-confirmed';
      booking: ConfirmedBooking;
      appointmentType?: Partial<AppointmentTypeState>;
    }
  | { type: 'submission-server-error'; errorText?: string }
  | { type: 'submission-pricing-error'; validateRequired?: boolean }
  | {
      type: 'choose-another-time';
      dismiss?: boolean;
      bannerText?: string;
    }
  | {
      type: 'verification-required' | 'duplicate-confirmation-required';
      dismiss?: boolean;
    }
  | { type: 'verification-error'; errorText?: string }
  | { type: 'update-voucher-questions'; questions?: VoucherAdditionalQuestion[] }
  | { type: 'update-referrals'; files: ReferralUploadFile[] }
  | { type: 'payment-error'; paymentError?: PaymentError };

export const formStateReducer = (state: FormState, action: Action): FormState => {
  switch (action.type) {
    case 'overwrite-state':
      return action.state;
    case 'assert-vaccine-eligibility':
      return { ...state, meta: { ...state.meta, vaccineEligibilityAssertedByUser: true } };
    case 'update-practice':
      return {
        ...state,
        appointment: {
          ...state.appointment,
          practice: {
            ...state.appointment.practice,
            ...action.partial,
          },
        },
      };
    case 'update-meta':
      return {
        ...state,
        meta: {
          ...state.meta,
          ...action.partial,
        },
      };
    case 'update-practitioner':
      // Practitioner updates should be in full and not partial
      // to prevent presentation of incorrect info to the user
      // if the practitioner changes and only the ID is updated
      return {
        ...state,
        appointment: {
          ...state.appointment,
          apptDoctor: action.apptDoctor,
        },
      };
    case 'update-appointment':
      return {
        ...state,
        appointment: {
          ...state.appointment,
          ...action.partial,
        },
      };
    case 'update-user':
      return {
        ...state,
        user: {
          ...state.user,
          ...action.partial,
        },
        meta: {
          ...state.meta,
          validationErrors: resetValidationForFields(
            state.meta.validationErrors,
            'user',
            action.partial,
          ),
        },
      };
    case 'update-patient':
      return {
        ...state,
        patient: {
          ...state.patient,
          ...action.partial,
        },
        meta: {
          ...state.meta,
          validationErrors: resetValidationForFields(
            state.meta.validationErrors,
            'patient',
            action.partial,
          ),
        },
      };
    case 'update-payment-method':
      return {
        ...state,
        paymentMethod: {
          ...state.paymentMethod,
          ...action.partial,
        },
        meta: {
          ...state.meta,
          validationErrors: resetValidationForFields(
            state.meta.validationErrors,
            'paymentMethod',
            action.partial,
          ),
        },
      };
    case 'continue':
      return {
        ...state,
        meta: {
          ...state.meta,
          // can't prevent moving out of bounds because of race condition
          // between updating steps list and moving forward
          // handle upper bound in components with useCurrentStep
          currentStep: action.fromStepIndex + 1,
          validationErrors: undefined,
        },
      };

    case 'back': {
      const { currentStep = 0, totalSteps = 0 } = state.meta;
      // prevent moving out of bounds
      const nextStep = Math.max(0, Math.min(currentStep - 1, totalSteps - 2));

      return {
        ...state,
        meta: {
          ...state.meta,
          currentStep: nextStep,
          validationErrors: undefined,
        },
      };
    }

    case 'back-to-start':
      return {
        ...state,
        meta: {
          ...state.meta,
          currentStep: 0,
          validationErrors: undefined,
        },
      };
    case 'validation-error':
      return {
        ...state,
        meta: {
          ...state.meta,
          validationErrors: {
            ...state.meta.validationErrors,
            ...action.errors,
          },
        },
      };
    case 'verification-required':
      return {
        ...state,
        meta: {
          ...state.meta,
          isVerificationRequired: action.dismiss !== true,
        },
      };
    case 'duplicate-confirmation-required':
      return {
        ...state,
        meta: {
          ...state.meta,
          isDuplicateConfirmationRequired: action.dismiss !== true,
        },
      };
    case 'payment-error':
      return {
        ...state,
        meta: {
          ...state.meta,
          paymentError: action.paymentError,
        },
      };
    case 'submission-pricing-error':
      return {
        ...state,
        meta: {
          ...state.meta,
          validatePrice: action.validateRequired,
        },
      };
    case 'submission-server-error':
      return {
        ...state,
        meta: {
          ...state.meta,
          submissionServerErrorText: action.errorText,
        },
      };
    case 'choose-another-time':
      return action.dismiss
        ? {
            ...state,
            meta: {
              ...state.meta,
              selectedAppointmentIsUnavailable: false,
              unavailableAppointmentDateString: undefined,
              appointmentBannerText: undefined,
            },
          }
        : {
            ...state,
            appointment: {
              ...state.appointment,
              dateString: undefined,
            },
            meta: {
              ...state.meta,
              selectedAppointmentIsUnavailable: true,
              unavailableAppointmentDateString: state.appointment.dateString,
              appointmentBannerText: action.bannerText,
            },
          };
    case 'goto-step':
      return {
        ...state,
        meta: {
          ...state.meta,
          currentStep: action.stepIndex,
        },
      };
    case 'booking-confirmed':
      return {
        ...state,
        appointment: {
          ...state.appointment,
          dateString: undefined,
          appointmentType:
            state.appointment.appointmentType && action.booking.followUpAppointmentInfo?.apptType
              ? {
                  ...(state.appointment.appointmentType ?? {}),
                  name: action.booking.followUpAppointmentInfo?.apptType,
                }
              : state.appointment.appointmentType,
        },
        meta: {
          ...state.meta,
          confirmedBookings: [...(state.meta.confirmedBookings || []), action.booking],
        },
      };
    case 'verification-error':
      return {
        ...state,
        meta: {
          ...state.meta,
          verificationErrorText: action.errorText,
        },
      };
    case 'update-voucher-questions':
      return {
        ...state,
        paymentMethod: {
          ...state.paymentMethod,
          voucherAdditionalQuestions: action.questions,
        },
      };
    case 'update-referrals':
      return {
        ...state,
        referrals: action.files,
      };
    // No default case so that eslint catches a non-exhaustive list of cases.
    // Add a default case in the future if needed to handle nested reducers.
  }
};

const resetValidationForFields = (
  currentErrors: FormState['meta']['validationErrors'],
  entityKey: string,
  fieldsToReset: object,
) => {
  if (!currentErrors) return;

  const fieldKeys = Object.keys(fieldsToReset).map(fieldKey => `$.${entityKey}.${fieldKey}`);

  const errorKeysNotUpdated = Object.keys(currentErrors).filter(
    currentErrorKey => fieldKeys.indexOf(currentErrorKey) === -1,
  );

  if (errorKeysNotUpdated.length === 0) return;
  return errorKeysNotUpdated.reduce(
    (errors, errorKey) => ({
      ...errors,
      [errorKey]: currentErrors[errorKey],
    }),
    {},
  );
};
